import {
  Component,
  Input,
  OnInit,
  AfterViewChecked,
  ElementRef,
  DoCheck,
  ViewChild,
  HostListener,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import { FilterType, BfxBlotterFilterId, BfxBlotterFilter } from "./bfx-blotter-filter.constants";
import { BfxBlotterFilterService } from "./bfx-blotter-filter.service";
import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { BfxBlotterFilterTemperatureComponent } from "app/bfx-blotter-filter/bfx-blotter-filter-temperature.component";
import { BfxBlotterFilterable } from "./bfx-blotter-filterable";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ExtendedOrderTypeIdentifier } from "./models/bfx-blotter-filter.constants";
import { Subject } from "rxjs";
import { throttleTime } from "rxjs/operators";
import {
  BfxBlotterFilterBarItem,
  BfxBlotterFilterBarData,
  BLOTTER_FILTER_TEMPERATURE_NODE_NAME,
  SELECT_SEPARATOR_ITEM,
} from "./bfx-blotter-filter-bar.constants";
import { BmarkContainerBlotterId } from "./models/bmark-container-blotter-id.enum";
import { TieredMenu } from "@bfx/primeng/tieredmenu";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { FilterConfigurationEvent } from "./FilterConfiguration";

/** Class applied to HTML element containing the name of the filter - used for matching view to model */
const FILTER_NAME_CLASS = "bfx-tag-label";

@Component({
  selector: ".bfx-blotter-filter-bar",
  templateUrl: "./html/bfx-blotter-filter-bar.html",
  standalone: false,
})
export class BfxBlotterFilterBarComponent
  implements OnInit, DoCheck, AfterViewInit, AfterViewChecked
{
  @Input()
  public blotterInstance: BfxBlotterFilterable = null;

  @Input()
  public set extendedTypeId(id: ExtendedOrderTypeIdentifier) {
    this._extendedTypeId = id;
  }

  public get extendedTypeId() {
    return this._extendedTypeId != null ? this._extendedTypeId : ExtendedOrderTypeIdentifier.OTHER;
  }

  @ViewChild("temperature")
  public temperatureFilter!: BfxBlotterFilterTemperatureComponent;

  @ViewChild("stateFilter")
  public stateFilter!: BfxBlotterFilterTemperatureComponent;

  private checkOverflowSubject: Subject<void> = new Subject<void>();

  public blotterId: string = null;
  public blotterType: BlotterType = BlotterType.ORDER;
  public filterConfiguration: string[] = null;

  public filters: Array<BfxBlotterFilter> = null;
  public favoritedFilters: Array<BfxBlotterFilter> = null;
  public items: Array<BfxBlotterFilterBarItem> = [];
  /** Filters that are not visible */
  private dropDownItems: Array<BfxBlotterFilterBarItem> = [];
  /** Menu items for filters that are not visible */
  public menuItems: Array<any> = [];
  public overflows: boolean = false;
  public showDropdown: boolean = false;
  public showTemperature: boolean = false;
  public showStateFilter: boolean = false;
  public window: NativeDesktopWindow = null;
  public lastShowTime: number = 0;
  private _extendedTypeId: ExtendedOrderTypeIdentifier;

  @ViewChild(TieredMenu) menu: TieredMenu;
  @ViewChild("menuItemsContainer") menuContainer: ElementRef;

  constructor(
    public service: BfxBlotterFilterService,
    public element: ElementRef,
    private goldenLayoutServices: GoldenLayoutService,
    private ref: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.service.modelUpdateSubject.subscribe(this.refresh.bind(this));
    this.service.filterConfiguration$.subscribe(this.handleFilterConfiguration.bind(this));
    this.service.filterFavoriteSubject.subscribe(this.handleFilterFavorite.bind(this));
    this.service.resizeSubject.subscribe(this.handleResize.bind(this));
    this.checkOverflowSubject.pipe(throttleTime(300)).subscribe(this.updateOverflow.bind(this));
  }

  ngDoCheck() {
    this.checkOverflow();
  }

  ngAfterViewInit() {
    this.initializeBlotterInstance();
  }

  ngAfterViewChecked() {
    // Avoid ExpressionChangedAfterItHasBeenCheckedError
    let showTemperature: boolean = this.blotterInstance.shouldShowTemperatureFilter();
    if (showTemperature != this.showTemperature) {
      this.showTemperature = showTemperature;
      this.ref.detectChanges();
    }

    if (this.blotterInstance?.shouldShowStateFilter) {
      let showStateFilter: boolean = this.blotterInstance.shouldShowStateFilter();
      if (showStateFilter != this.showStateFilter) {
        this.showStateFilter = showStateFilter;
        this.ref.detectChanges();
      }
    } else {
      logger.out.warn(
        `${this.constructor.name}: Blotter instance` +
          ` ${this.blotterInstance?.constructor?.name} does not implement BfxBlotterFilterable`,
      );
    }
  }

  protected initializeBlotterInstance() {
    if (this.blotterInstance != null) {
      this.blotterId = this.blotterInstance.getBlotterId();
      this.blotterType = this.blotterInstance.getBlotterType();
      // this.showTemperature = this.blotterInstance.shouldShowTemperatureFilter();
      this.filterConfiguration = this.blotterInstance.getFilterConfiguration();
      this.refresh(false, true);
      this.blotterInstance.setBlotterFilterComponent(this);
      // this.ref.markForCheck();
    }
  }

  protected handleFilterConfiguration(event: FilterConfigurationEvent): void {
    if (this.filterConfiguration == null && this.blotterInstance != null) {
      this.filterConfiguration = this.blotterInstance.getFilterConfiguration();
    }

    if (this.blotterInstance != null && this.blotterId == null) {
      this.blotterId = this.blotterInstance.getBlotterId();
    }

    if (this.filterConfiguration == null) {
      this.filterConfiguration = [];
    }

    if (
      event.blotterId == this.blotterId ||
      BmarkContainerBlotterId.BMARK_SUMMARY == event.blotterId ||
      BmarkContainerBlotterId.HEDGE_ORDER_MONITOR == event.blotterId
    ) {
      event.items.forEach((item: string) => {
        if (this.filterConfiguration.indexOf(item) < 0) {
          this.filterConfiguration.push(item);
        }
      });
      this.refresh(true);
    }

    if (this.temperatureFilter != null) {
      this.temperatureFilter.writeValue(this.blotterInstance.getTemperatureSetting());
    }

    if (this.stateFilter != null) {
      this.stateFilter.writeValue(this.blotterInstance.getStateFilter());
    }

    // this.ref.markForCheck();
  }

  protected handleFilterFavorite(event: any): void {
    if (event.blotterTypeId) {
      let blotterTypeId: BfxBlotterFilterId = event.blotterTypeId;
      if (
        blotterTypeId.blotterType == this.blotterType &&
        this.extendedTypeId == event.extendedTypeId
      ) {
        this.refresh();
      }
    }
    // this.ref.markForCheck();
  }

  protected handleResize(event: any): void {
    if (this.window != null) {
      this.window.hide();
      return;
    }

    this.showDropdown = false;
    this.checkOverflow();
  }

  public refresh(updateFiltersAfterwards: boolean = false, initRefresh: boolean = false): void {
    let filterId: BfxBlotterFilterId = new BfxBlotterFilterId(this.blotterType, AssetClass.FIX);
    this.service
      .getItems(FilterType.BlotterFilter, filterId, this.extendedTypeId)
      .then((filters: Array<BfxBlotterFilter>) => {
        this.filters = filters;
        this.service
          .getFavoriteFilters(filterId, this.extendedTypeId)
          .then((favoritedFilterIds: Array<string>) => {
            this.items = new Array<BfxBlotterFilterBarItem>();
            // because this.filterConfiguration is not set on init for bmarkSummary
            // and HOM is set from bmarkSummary not from its blotterInstance
            this.filterConfiguration =
              initRefresh && this.blotterId != BmarkContainerBlotterId.HEDGE_ORDER_MONITOR
                ? this.blotterInstance.getFilterConfiguration()
                : this.filterConfiguration;
            let selected: Array<string> = this.filterConfiguration;

            filters.forEach((filter: BfxBlotterFilter) => {
              let item: BfxBlotterFilterBarItem = new BfxBlotterFilterBarItem();

              item.filterId = filter.filterId;
              item.filterName = filter.filterName;
              item.favorited = favoritedFilterIds.indexOf(filter.filterId) >= 0;
              item.selected = selected != null && selected.indexOf(filter.filterId) >= 0;

              if (item.favorited || item.selected) {
                this.items.push(item);
              }
            });

            this.updateItems();

            if (updateFiltersAfterwards && this.blotterInstance.filterSelectionChanged) {
              this.blotterInstance.filterSelectionChanged(this.filterConfiguration);
            }
          });
      });
    // this.ref.markForCheck();
  }

  public get allItems(): Array<BfxBlotterFilterBarItem> {
    return this.items;
  }

  public selectItem(selectedItem: BfxBlotterFilterBarItem, event: any): void {
    selectedItem.selected = !selectedItem.selected;

    if (!event.ctrlKey && !event.originalEvent?.ctrlKey && selectedItem.selected) {
      this.items.forEach(
        (item: BfxBlotterFilterBarItem) =>
          (item.selected = selectedItem.filterName == item.filterName),
      );
    }

    // Cleanup un-favourited filters
    this.items = this.items.filter(
      (item: BfxBlotterFilterBarItem) => item.favorited || item.selected,
    );

    // sync decoupled bmark blotterInstances
    if (this.extendedTypeId === ExtendedOrderTypeIdentifier.BENCHMARK) {
      if (this.blotterInstance.getBlotterId() === BmarkContainerBlotterId.BMARK_SUMMARY) {
        this.syncFiltersFromBmarkSummary();
      } else if (
        this.blotterInstance.getBlotterId() === BmarkContainerBlotterId.HEDGE_ORDER_MONITOR
      ) {
        this.syncFiltersFromBmarkHom(selectedItem);
      }
    }

    this.updateItems();

    let index: number = this.filterConfiguration.indexOf(selectedItem.filterId);

    if (index < 0 && selectedItem.selected) {
      this.filterConfiguration.push(selectedItem.filterId);
    } else if (index >= 0 && !selectedItem.selected) {
      this.filterConfiguration.splice(index, 1);
    }

    if (this.blotterInstance.filterSelectionChanged) {
      this.blotterInstance.filterSelectionChanged(this.filterConfiguration);
      if (this.blotterInstance.hasCellRenderedSelection()) {
        this.blotterInstance.refreshSelectionCells();
      }
    }

    this.checkOverflow();

    if (this.window != null) {
      this.window.hide();
    }
  }

  public getSelectedItems(): Array<BfxBlotterFilterBarItem> {
    return this.items.filter((item: BfxBlotterFilterBarItem) => item.selected);
  }

  public getSelectedFilters(): Array<BfxBlotterFilter> {
    let selectedFilterIds: Array<string> = this.items
      .filter((item: BfxBlotterFilterBarItem) => item.selected)
      .map((item: BfxBlotterFilterBarItem) => item.filterId);
    return this.filters
      ? this.filters.filter(
          (filter: BfxBlotterFilter) => selectedFilterIds.indexOf(filter.filterId) >= 0,
        )
      : [];
  }

  public updateItems(): void {
    // Perform a sort operation
    this.items.sort(this.compareItems);

    // Insert separator
    this.insertSelectionSeparatorItem();

    // Update filter configuration
    this.updateFilterConfiguration();

    // Check if bar overflows and show dropdown
    this.checkOverflow();

    //update tabs title
    this.updateTabs();
  }

  protected compareItems(itemA: BfxBlotterFilterBarItem, itemB: BfxBlotterFilterBarItem): number {
    return itemA.selected == itemB.selected
      ? itemA.filterName.localeCompare(itemB.filterName)
      : itemA.selected
        ? -1
        : 1;
  }

  protected insertSelectionSeparatorItem(): void {
    if (this.items.length > 0 && this.items[0].selected) {
      for (let i: number = 0; i < this.items.length - 1; i++) {
        if (this.items[i].selected != this.items[i + 1].selected) {
          this.items.splice(i + 1, 0, SELECT_SEPARATOR_ITEM);
          break;
        }
      }
    }
  }

  protected updateFilterConfiguration(): void {
    if (this.filterConfiguration != null) {
      this.filterConfiguration.splice(0, this.filterConfiguration.length);
    } else {
      this.filterConfiguration = [];
    }

    // Filter ID for logging
    const logIds = [];
    this.items.forEach((item: BfxBlotterFilterBarItem) => {
      if (item.selected) {
        this.filterConfiguration.push(item.filterId);
        logIds.push(`${item.filterName}(${item.filterId})`);
      }
    });

    // Log out the active list of filters
    logger.out.info(
      `bfb-c - ${this.blotterId}: applying ${logIds.length} filters [${logIds.join(",")}]`,
    );
  }

  protected checkOverflow(): void {
    this.checkOverflowSubject.next();
  }

  /**
   * Check locations of all filter items. If any are positioned past width of the container then
   * add to an overflow list
   */
  protected updateOverflow(): void {
    if (!this.menuContainer?.nativeElement) {
      return;
    }
    let nodes: Array<any> = this.menuContainer.nativeElement.childNodes;

    let overflow: boolean = false;
    // clean-up non-filter nodes
    let clearedNodeList = Object.values(nodes).filter(
      (elem: HTMLDivElement) =>
        elem instanceof HTMLDivElement && elem.nodeName !== BLOTTER_FILTER_TEMPERATURE_NODE_NAME,
    );

    this.dropDownItems = [];
    const containerWidth = this.menuContainer.nativeElement.clientWidth;
    for (let i: number = 0; i < clearedNodeList.length; i++) {
      const filterNode = clearedNodeList[i] as HTMLDivElement;

      const left = filterNode.offsetLeft;
      const width = filterNode.offsetWidth;

      let isNodeOverflowed: boolean = left && width && left + width > containerWidth;
      if (isNodeOverflowed) {
        filterNode.classList.add("item-overflow");
      } else {
        filterNode.classList.remove("item-overflow");
      }
      if (isNodeOverflowed) {
        const filterName = this.getFilterNameFromFilterItem(filterNode);
        if (filterName) {
          let dropDown: Array<BfxBlotterFilterBarItem> = this.items.filter(
            (item: BfxBlotterFilterBarItem) => item.filterName == filterName,
          );
          if (dropDown.length == 1) {
            this.dropDownItems.push(dropDown[0]);
            overflow = true;
          }
        }
      }
    }
    this.overflows = overflow;
  }

  /**
   * Extract the filter name from the displayed text of the filter item
   * @param filterNode
   * @returns
   */
  private getFilterNameFromFilterItem(filterNode: HTMLDivElement): string {
    const filterNameNodeList = filterNode.getElementsByClassName(FILTER_NAME_CLASS);
    if (filterNameNodeList.length == 1 && filterNameNodeList[0] instanceof HTMLElement) {
      return filterNameNodeList[0].textContent?.trim();
    }
    return null;
  }

  public onClickToggle($event): void {
    this.menuItems = this.constructMenu();
    this.menu.toggle($event);
  }

  private constructMenu() {
    const retItems: any[] = this.dropDownItems.map((item) => {
      return {
        label: item.filterName,
        command: (event) => this.selectItem(item, event),
        show: true,
        styleClass: item.selected ? "filter-selected" : "filter-unselected",
      };
    });
    return retItems;
  }

  private updateTabs(): void {
    if (this.blotterInstance.getBlotterId() === "blotter-inbox") return;
    let title: string = "";
    if (this.items.length == 0) title = "";
    else {
      let appliedFilters: Array<any> = this.items.filter((filter) => filter.selected);
      appliedFilters.forEach((filter, index) => {
        title +=
          index != appliedFilters.length - 1 ? filter.filterName + ", " : filter.filterName + " ";
      });
    }
    switch (this.blotterType) {
      case BlotterType.ORDER:
        this.goldenLayoutServices.updateOrderBlotterTabs(
          title,
          this.blotterId,
          this.extendedTypeId,
        );
        break;
      case BlotterType.TRADE:
        this.goldenLayoutServices.updateClientTradeBlotterTabs(title, this.blotterId);
        break;
      case BlotterType.RISKTRADE:
        this.goldenLayoutServices.updateRiskTradeBlotterTabs(title, this.blotterId);
        break;
      case BlotterType.INTERVENTION:
        this.goldenLayoutServices.updateInterventionBlotterTabs(title, this.blotterId);
        break;
    }
  }

  private syncFiltersFromBmarkSummary() {
    let bmarkSummaryComponent = this.blotterInstance;
    let hedgeOrderMonitorComponents = bmarkSummaryComponent.subBlotterComponents.filter(
      (subComponent) => subComponent.getBlotterId() === BmarkContainerBlotterId.HEDGE_ORDER_MONITOR,
    );

    if (hedgeOrderMonitorComponents != null && hedgeOrderMonitorComponents.length > 0) {
      hedgeOrderMonitorComponents.forEach(
        (component) => (component.blotterFilterBar.items = this.items),
      );
    }
  }

  syncFiltersFromBmarkHom(selectedItem) {
    let hedgeOrderMonitorComponent = this.blotterInstance;
    let data: BfxBlotterFilterBarData = {
      items: this.items,
      selectedItem: selectedItem,
      blotterId: BmarkContainerBlotterId.BMARK_SUMMARY,
    };

    hedgeOrderMonitorComponent.syncBlotterFilterBar(data);
  }
}
